import dayjs, { isDayjs, type Dayjs } from "dayjs";
import isUndefined from "lodash/isUndefined";
import omitBy from "lodash/omitBy";

import type {
    CommonWebUserdataAbstractionsRoleBasedAssetRole,
    ContractPlatformCampaignsScrapingConfiguration,
    ProWebsiteRankTrackerModelRankTrackerCreateCampaignRequest,
    RankTrackerCampaignDetails,
    RankTrackerContractRankTrackerCampaign,
    RankTrackerContractRankTrackerCampaignEntities,
    RankTrackerContractTrackingScope,
    RankTrackerContractTrackingScopeRequest,
    RankTrackerUpdateCampaignRequest,
    RtBulkUpdateCampaignPermissionsPayload,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { swSettings } from "app/common/services/swSettings";
import type {
    ICampaignsBaseScrapingConfiguration,
    ICampaignsScrapingConfiguration,
} from "app/components/campaigns/shared/CampaignsConst";
import type { AssetUser } from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/EditCampaignSettings.consts";
import { EAssetRole } from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/EditCampaignSettings.consts";
import type { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type {
    IKeywordsCampaignsCampaignRawData,
    IKeywordsCampaignsUpdateCampaignData,
    IRankTrackerCompetitor,
    IRTBaseCampaignWithSC,
    IRTCurrentCampaignData,
} from "./KeywordsCampaignTypes";

type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? A : B;

type WritableKeys<T> = {
    [P in keyof T]-?: IfEquals<{ [Q in P]: T[P] }, { -readonly [Q in P]: T[P] }, P>;
}[keyof T];

type ReadonlyKeys<T> = {
    [P in keyof T]-?: IfEquals<{ [Q in P]: T[P] }, { -readonly [Q in P]: T[P] }, never, P>;
}[keyof T];

/** Writable keys stay as generated. OpenAPI `readOnly` keys may be omitted on write. */
type WithOptionalReadonly<T> = Pick<T, WritableKeys<T>> & Partial<Pick<T, ReadonlyKeys<T>>>;

type ScrapingConfigurationWrite = WithOptionalReadonly<ContractPlatformCampaignsScrapingConfiguration>;

/** Server sets these on create; OpenAPI still marks them required on the generated request type. */
type CreateCampaignWriteBody = Omit<
    ProWebsiteRankTrackerModelRankTrackerCreateCampaignRequest,
    "createdByUserId" | "createdTime" | "keywordCount" | "updatedTime"
>;

type UpdateCampaignSettingsWrite = Omit<RankTrackerUpdateCampaignRequest, "addedScrapingConfigurations"> & {
    addedScrapingConfigurations?: ScrapingConfigurationWrite[] | null;
};

type CampaignEntities = {
    [entity: string]: string[];
};

type BulkPermissionsInput = Array<{
    campaignId: string;
    assetRolesSettings?: { userAssetRoles?: AssetUser[] | null } | null;
}>;

const toUtcDayjs = (value: Dayjs | string | null | undefined): Dayjs | undefined => {
    if (!value) {
        return undefined;
    }
    const parsed = isDayjs(value) ? value : dayjs.utc(value);
    return parsed.isValid() ? parsed : undefined;
};

const reportInvalidCampaign = (message: string): void => {
    Sentry.errorTracker({
        e: new Error(message),
        message,
        level: "error",
    });
};

const toUiDevice = (device: string): DEVICES_TYPES_FOR_API => device as DEVICES_TYPES_FOR_API;

const toUiScrapingConfiguration = (config: {
    id?: string | null;
    searchEngine?: string | null;
    geoLocation: string;
    device: string;
    locale?: string | null;
    geoLocationType: string;
    geoLocationCountry: number;
    keywords?: string[];
}): ICampaignsScrapingConfiguration => ({
    id: config.id ?? undefined,
    searchEngine: config.searchEngine ?? "",
    geoLocation: config.geoLocation,
    device: toUiDevice(config.device),
    locale: config.locale ?? "",
    geoLocationType: config.geoLocationType,
    keywords: "keywords" in config && config.keywords ? config.keywords : [],
    geoLocationCountry: config.geoLocationCountry,
});

const normalizeStringListMap = (
    value: Record<string, string[] | null> | null | undefined,
): Record<string, string[]> => {
    if (!value) {
        return {};
    }
    return Object.fromEntries(Object.entries(value).map(([key, items]) => [key, items ?? []]));
};

const normalizeEntities = (
    entities: RankTrackerContractRankTrackerCampaignEntities | null | undefined,
): CampaignEntities => ({
    youTubeChannels: entities?.youTubeChannels ?? [],
    googleBusinessProfiles: entities?.googleBusinessProfiles ?? [],
    tikTokChannels: entities?.tikTokChannels ?? [],
    twitterAccounts: entities?.twitterAccounts ?? [],
});

const toUiAssetRoles = (
    settings: RankTrackerContractRankTrackerCampaign["assetRolesSettings"],
): IRTCurrentCampaignData["assetRolesSettings"] => {
    if (!settings?.userAssetRoles) {
        return undefined;
    }
    return {
        userAssetRoles: settings.userAssetRoles.map((role) => ({
            userId: role.userId,
            assetRole: role.assetRole as EAssetRole,
        })),
    };
};

const competitorSites = (competitors: Array<string | IRankTrackerCompetitor> | null | undefined): string[] =>
    (competitors ?? []).map((competitor) => (typeof competitor === "string" ? competitor : competitor.site));

type CampaignIdAndDates = {
    id: string;
    createdTime: Dayjs;
    updatedTime: Dayjs;
    lastMigrationDate: Dayjs | null;
};

const requireCampaignIdAndDates = (
    campaign: {
        id?: string | null;
        createdTime: string;
        updatedTime: string;
        lastMigrationDate?: string | null;
    },
    missingIdMessage: string,
): CampaignIdAndDates | undefined => {
    if (!campaign.id) {
        reportInvalidCampaign(missingIdMessage);
        return undefined;
    }

    const createdTime = toUtcDayjs(campaign.createdTime);
    const updatedTime = toUtcDayjs(campaign.updatedTime);
    if (!createdTime || !updatedTime) {
        reportInvalidCampaign(`Rank Tracker campaign ${campaign.id} has an invalid created or updated time`);
        return undefined;
    }

    return {
        id: campaign.id,
        createdTime,
        updatedTime,
        lastMigrationDate: toUtcDayjs(campaign.lastMigrationDate) ?? null,
    };
};

const toCampaignListItem = (
    item: RankTrackerCampaignDetails,
    flags: { isShared: boolean; isDemo?: boolean },
): IKeywordsCampaignsCampaignRawData | undefined => {
    const identity = requireCampaignIdAndDates(item, "Rank Tracker campaign list item is missing an id");
    if (!identity) {
        return undefined;
    }
    if (!item.name || !item.site) {
        reportInvalidCampaign(`Rank Tracker campaign ${identity.id} is missing a name or site`);
        return undefined;
    }

    const row: IKeywordsCampaignsCampaignRawData = {
        id: identity.id,
        name: item.name,
        site: item.site,
        competitors: item.competitors ?? [],
        isShared: flags.isShared,
        createdTime: identity.createdTime,
        keywordCount: item.keywordCount,
        firstScrapeTime: item.firstScrapeTime,
        lastScrapeDate: item.lastScrapeDate ?? null,
        tags: {},
        keywordToTargetUrls: {},
        scrapingConfigurations: (item.scrapingConfigurations ?? []).map(toUiScrapingConfiguration),
        createdBy: item.createdBy ?? "",
        entities: {},
        trackingScope: item.trackingScope,
        isEditable: item.isEditable,
        assetRolesSettings: toUiAssetRoles(item.assetRolesSettings),
        ...(flags.isDemo ? { isDemo: true } : {}),
    };

    return row;
};

export const mapCampaignList = (
    campaigns: RankTrackerCampaignDetails[] | null | undefined,
    flags: { isShared: boolean; isDemo?: boolean },
): IKeywordsCampaignsCampaignRawData[] =>
    (campaigns ?? []).flatMap((campaign) => {
        const mapped = toCampaignListItem(campaign, flags);
        return mapped ? [mapped] : [];
    });

export const mapCurrentCampaign = (
    campaign: RankTrackerContractRankTrackerCampaign,
    options: { isShared?: boolean; isDemo?: boolean } = {},
): IRTCurrentCampaignData => {
    const identity = requireCampaignIdAndDates(campaign, "Rank Tracker campaign response is missing an id");
    if (!identity) {
        throw new Error(`Rank Tracker campaign ${campaign.id ?? "unknown"} is missing required fields`);
    }

    const isEditable = campaign.assetRolesSettings?.userAssetRoles?.some((user) => user.userId === swSettings.user.id);

    const mapped = {
        id: identity.id,
        name: campaign.name,
        site: campaign.site,
        competitors: campaign.competitors ?? [],
        entities: normalizeEntities(campaign.entities),
        tags: normalizeStringListMap(campaign.tags),
        keywordToTargetUrls: normalizeStringListMap(campaign.keywordToTargetUrls),
        trackingScope: campaign.trackingScope,
        scrapingConfigurations: campaign.scrapingConfigurations.map(toUiScrapingConfiguration),
        createdTime: identity.createdTime,
        updatedTime: identity.updatedTime,
        lastMigrationDate: identity.lastMigrationDate,
        firstScrapeTime: campaign.firstScrapeTime,
        lastScrapeDate: campaign.lastScrapeDate ?? null,
        isActive: campaign.isActive ?? false,
        isShared: options.isShared ?? false,
        createdByUserId: campaign.createdByUserId,
        keywordCount: campaign.keywordCount,
        isDemo: options.isDemo,
        isEditable,
        assetRolesSettings: toUiAssetRoles(campaign.assetRolesSettings),
        // The wire SERP union is wider than ESerpTypes. Unmodelled values must survive so a
        // settings save does not un-exclude features the UI enum does not list.
        positionExcludedSerpFeatures: (campaign.positionExcludedSerpFeatures ?? undefined) as ESerpTypes[] | undefined,
    };

    // Favicon enrichment still turns these competitor site strings into icon rows.
    return mapped as unknown as IRTCurrentCampaignData;
};

const TRACKING_SCOPE_TO_REQUEST = {
    0: "SubDomain",
    1: "MainDomain",
    2: "Subfolder",
    3: "ExactUrl",
} as const satisfies Record<RankTrackerContractTrackingScope, RankTrackerContractTrackingScopeRequest>;

/** `domain` and `subdomain` both arrive as `0`, and both are sent as `SubDomain`. */
export const toCreateTrackingScope = (
    scope: RankTrackerContractTrackingScope | undefined,
): RankTrackerContractTrackingScopeRequest => TRACKING_SCOPE_TO_REQUEST[scope ?? 0];

const omitUndefined = <T extends object>(obj: T): T => omitBy(obj, isUndefined) as T;

const toWireAssetRole = (role: number): CommonWebUserdataAbstractionsRoleBasedAssetRole => {
    switch (role) {
        case EAssetRole.Editor:
            return 1;
        case EAssetRole.Owner:
            return 2;
        case EAssetRole.Viewer:
        default:
            return 0;
    }
};

const toScrapingConfigurationWrite = (
    config: ICampaignsBaseScrapingConfiguration & { geoLocationCountry?: number; name?: string | null },
) =>
    omitUndefined({
        geoLocation: config.geoLocation,
        device: config.device,
        geoLocationType: config.geoLocationType,
        keywords: config.keywords,
        geoLocationCountry: config.geoLocationCountry !== undefined ? config.geoLocationCountry : undefined,
        id: config.id || undefined,
        name: config.name || undefined,
        searchEngine: config.searchEngine || undefined,
        locale: config.locale || undefined,
    });

type AddCampaignRequestBody = Omit<CreateCampaignWriteBody, "scrapingConfigurations"> & {
    scrapingConfigurations: ReturnType<typeof toScrapingConfigurationWrite>[];
};

export const toAddCampaignRequest = (
    payload: IRTBaseCampaignWithSC,
): ProWebsiteRankTrackerModelRankTrackerCreateCampaignRequest => {
    const body = {
        name: payload.name,
        site: payload.site,
        competitors: competitorSites(payload.competitors),
        tags: payload.tags,
        trackingScope: toCreateTrackingScope(payload.trackingScope as RankTrackerContractTrackingScope),
        scrapingConfigurations: payload.scrapingConfigurations.map(toScrapingConfigurationWrite),
        keywordToTargetUrls: payload.keywordToTargetUrls,
        entities: payload.entities,
    } satisfies AddCampaignRequestBody;
    // Generated rtAddCampaign param still requires server fields. RT sets them; the write body omits them.
    return body as unknown as ProWebsiteRankTrackerModelRankTrackerCreateCampaignRequest;
};

export const toUpdateCampaignSettingsRequest = (
    payload: IKeywordsCampaignsUpdateCampaignData,
): RankTrackerUpdateCampaignRequest => {
    const body = omitUndefined({
        assetRolesSettings: {
            userAssetRoles: (payload.assetRolesSettings.userAssetRoles ?? []).map((role) => ({
                userId: role.userId,
                assetRole: toWireAssetRole(role.assetRole),
            })),
        },
        name: payload.name,
        site: payload.site,
        competitors: payload.competitors,
        entities: payload.entities,
        keywordChanges: payload.keywordChanges,
        updatedTags: payload.updatedTags,
        updatedKeywordToTargetUrls: payload.updatedKeywordToTargetUrls,
        removedScrapingConfigurationGuids: payload.removedScrapingConfigurationGuids,
        addedScrapingConfigurations: payload.addedScrapingConfigurations
            ? payload.addedScrapingConfigurations.map(toScrapingConfigurationWrite)
            : undefined,
        positionExcludedSerpFeatures: payload.positionExcludedSerpFeatures,
    }) satisfies Omit<UpdateCampaignSettingsWrite, "addedScrapingConfigurations"> & {
        addedScrapingConfigurations?: ReturnType<typeof toScrapingConfigurationWrite>[];
    };
    // Generated params still require readonly keywordCount on added scrape configs. RT derives it from keywords.
    return body as unknown as RankTrackerUpdateCampaignRequest;
};

export const toBulkPermissionsRequest = (payload: BulkPermissionsInput): RtBulkUpdateCampaignPermissionsPayload =>
    payload.map((item) => ({
        campaignId: item.campaignId,
        assetRolesSettings: {
            userAssetRoles: (item.assetRolesSettings?.userAssetRoles ?? []).map((role) => ({
                userId: role.userId,
                assetRole: toWireAssetRole(role.assetRole),
            })),
        },
    }));
